import React, { useState, useEffect } from 'react';
import { Button } from 'react-bootstrap';
import axios from 'axios';
import CardPicker from './CardPicker';
import { useUser } from '../UserContext'; // Import the useUser hook

const emoticonMap = { "thinking": { srcset: 1 }, "woodenReno": { srcset: 1 }, "jaraxxus-thinking": { srcset: 1 }, "jaraxxus": { srcset: 1 }, "razorpetal": { srcset: 1 }, "bananas": {}, "annoy-o-tron": { srcset: 1 }, "aannoy-o-tron": { animated: 1 }, "smorc": { srcset: 1 }, "tinyfin": { srcset: 1 }, "common": { srcset: 1 }, "rare": { srcset: 1 }, "epic": { srcset: 1 }, "legendary": { srcset: 1 }, "boombot": { srcset: 1 }, "horde": { srcset: 1 }, "alliance": { srcset: 1 }, "snake": { srcset: 1 }, "boomS": { srcset: 1 }, "chicken": { srcset: 1 }, "wow": { srcset: 1 }, "finleySmile": { srcset: 1 }, "reno": { srcset: 1 }, "illidon": { srcset: 1 }, "bob": { srcset: 1 }, "cat": { srcset: 1 }, "shrugillL": { srcset: 1 }, "shrugillR": { srcset: 1 }, "voidw": { srcset: 1 }, "blingtron": { srcset: 1 }, "dhguy": { srcset: 1 }, "rainbow": { srcset: 1 }, "faelin": { srcset: 1 }, "skelehead": { srcset: 1 }, "treeh": { srcset: 1 }, "ghoul": { srcset: 1 }, "pensive": { srcset: 1 }, "trumpet": { srcset: 1 }, "scared": { srcset: 1 }, "frogface": { srcset: 1 }, "thumbsup": { srcset: 1 }, "clowno": { srcset: 1 }, "fintective": { srcset: 1 }, "rafaamPog": { srcset: 1 }, "finking": { srcset: 1 }, "gulpfingA": { srcset: 0, animated: 1 }, "shocked": { srcset: 1 }, "waveflipper": { srcset: 1 }, "wavedummy": { srcset: 1 }, "happyButtons": { srcset: 1 }, "lynessa": { srcset: 1 }, "murlocMarine": { srcset: 1 }, "kingPlush": { srcset: 1 }, "qnikkoHi": { srcset: 1 }, "aqnikkoHi": { srcset: 1, animated: 1 }, "treeLove": { srcset: 1 }, "qnikkoWhat": { srcset: 1 }, "qWOW": { srcset: 1 }, "aqWOW": { srcset: 1, animated: 1 }, "qLOVE": { srcset: 1 }, "qCRY": { srcset: 1 }, "qCozy": { srcset: 1 }, "qCool": { srcset: 1 }, "qTPie": { srcset: 1 }, "qSleep": { srcset: 1 } };

// 24 hours in milliseconds
const DRAFT_EXPIRATION_TIME = 24 * 60 * 60 * 1000;

const CommentForm = ({ cardInfo, onCommentSubmit, initialComment = '', commentReplyId = false, isReply = false }) => {
  const [comment, setComment] = useState(false);
  const [showEmotePicker, setShowEmotePicker] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const [selectedCards, setSelectedCards] = useState([]);
  const [isSticky, setIsSticky] = useState(false);
  const { user } = useUser(); // Destructure setSelectedUser from the context
  const [hasDraft, setHasDraft] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const getDraftKey = () => {
    return `draft_comment_${cardInfo.cardId}${cardInfo.cardId2 ? '_' + cardInfo.cardId2 : ''}${commentReplyId ? '_reply_' + commentReplyId : ''}`;
  };
  
    // Clean up expired drafts
    const cleanupExpiredDrafts = () => {
      const now = new Date().getTime();
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('draft_comment_')) {
          try {
            const draftData = JSON.parse(localStorage.getItem(key));
            if (now - draftData.timestamp > DRAFT_EXPIRATION_TIME) {
              localStorage.removeItem(key);
            }
          } catch (err) {
            console.error('Error cleaning up draft:', err);
            localStorage.removeItem(key); // Remove invalid drafts
          }
        }
      });
    };

  // Load draft on component mount
  useEffect(() => {
    cleanupExpiredDrafts();
    loadDraft();
  }, [cardInfo.cardId, cardInfo.cardId2, commentReplyId, initialComment]);

  const loadDraft = () => {
    const draftKey = getDraftKey();
    const savedDraft = localStorage.getItem(draftKey);
    
    if (savedDraft) {
      try {
        const draftData = JSON.parse(savedDraft);
        const now = new Date().getTime();
        
        // Check if draft has expired
        if (now - draftData.timestamp > DRAFT_EXPIRATION_TIME) {
          localStorage.removeItem(draftKey);
          setHasDraft(false);
          // Show initial comment if draft expired and we have one
          if (initialComment) {
            setComment(initialComment);
          } else {
            setComment('');
          }
          return;
        }
  
        // Always load draft if it exists and hasn't expired, regardless of initialComment
        setComment(draftData.comment);
        setSelectedCards(draftData.selectedCards || []);
        setIsSticky(draftData.isSticky || false);
        setHasDraft(true);
      } catch (err) {
        console.error('Error loading draft:', err);
        localStorage.removeItem(draftKey);
        setHasDraft(false);
        // Show initial comment if draft is corrupt and we have one
        if (initialComment) {
          setComment(initialComment);
        } else {
          setComment('');
        }
      }
    } else {
      // No draft exists, show initial comment if we have one
      setComment(initialComment || '');
      setHasDraft(false);
    }
  };

  // Save draft whenever content changes
  useEffect(() => {
    if (comment || selectedCards.length > 0) {
      const draftKey = getDraftKey();
      const draftData = {
        comment,
        selectedCards,
        isSticky,
        timestamp: new Date().getTime(),
      };
      localStorage.setItem(draftKey, JSON.stringify(draftData));
      setHasDraft(true);
    }
  }, [comment, selectedCards, isSticky]);

  const handleCommentChange = (e) => {
    setComment(e.target.value);
  };

  const clearDraft = () => {
    const draftKey = getDraftKey();
    localStorage.removeItem(draftKey);
    setComment('');
    setSelectedCards([]);
    setIsSticky(false);
    setHasDraft(false);
  };

  const handleCommentSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting) return; // Prevent multiple submissions
    setIsSubmitting(true);

    const userComment = {
      cardid: cardInfo.cardId,
      cardid2: cardInfo.cardId2,
      comment: comment,
      sticky: isSticky ? '1' : '',
      usercards: selectedCards,
      gimmick: '',
    };

    // Add commentReplyId to the request if it's available
    if (commentReplyId) {
      userComment.commentReplyId = commentReplyId;
    }

    try {
      const response = await axios.post('/api/comments/post-comment.php', userComment);
      if (response.data.success) {
        setComment('');
        setSelectedCards([]);
        clearDraft();
        onCommentSubmit(response.data.comment);
      } else {
        alert('Failed to post comment');
      }
    } catch (err) {
      console.error('Error posting comment:', err);
      alert('An error occurred while posting the comment');
    } finally {
      setIsSubmitting(false); // Re-enable the button after request completes
    }
  };

  const handleEmoteClick = (emote) => {
    const emoteText = `:${emote}:`;
    setComment((prevComment) => {
        const spaceBefore = prevComment && prevComment.slice(-1) !== ' ' ? ' ' : '';
        return prevComment + spaceBefore + emoteText + ' ';
    });
};


  

  const handleCardSelection = (cards) => {
    setSelectedCards(cards);
  };

  const showStickyOption = !isReply && Number(cardInfo.creatorId) === Number(user.userid);

  // Calculate time remaining for draft
  const getTimeRemaining = () => {
    const draftKey = getDraftKey();
    const savedDraft = localStorage.getItem(draftKey);
    if (savedDraft) {
      try {
        const draftData = JSON.parse(savedDraft);
        const now = new Date().getTime();
        const timeLeft = DRAFT_EXPIRATION_TIME - (now - draftData.timestamp);
        const hoursLeft = Math.floor(timeLeft / (60 * 60 * 1000));
        return hoursLeft > 0 ? `Draft expires in ${hoursLeft} hours` : 'Draft expires soon';
      } catch (err) {
        return '';
      }
    }
    return '';
  };

  return (
    <form onSubmit={handleCommentSubmit}>
      <div className="mb-0 text-start">
        <label htmlFor="newComment" className="form-label">
          {isReply ? 'Reply to comment' : 'Post a Comment'}
        </label>
        <div className="d-flex align-items-start position-relative">
          <textarea
            id="newComment"
            className="form-control"
            value={comment}
            onChange={handleCommentChange}
            rows="3"
            required
            style={{ paddingTop: "10px", paddingRight: "45px" }}
            maxlength="2000"
          ></textarea>
          <button
            type="button"
            className="btn btn-link position-absolute p-1"
            style={{ top: "2%", right: "0", fontSize: "0.75rem" }}
            onClick={() => setShowEmotePicker((prev) => !prev)}
          >
            <img 
              src="https://www.hearthcards.net/images/emotes/thinking.png" 
              srcSet="https://www.hearthcards.net/images/emotes/thinking.png 1x, https://www.hearthcards.net/images/emotes/thinking2.png 2x, https://www.hearthcards.net/images/emotes/thinking3.png 4x" 
              style={{ width: "25px", height: "25px" }} 
            />
          </button>
        </div>
        {showEmotePicker && (
          <div className="emote-picker active">
												{Object.keys(emoticonMap).map((emote) => {
													const isAnimated = emoticonMap[emote].animated;
													const imgSrc = `https://www.hearthcards.net/images/emotes/${emote}.${isAnimated ? 'gif' : 'png'}`;
													const imgSrcSet = isAnimated
														? null
														: `https://www.hearthcards.net/images/emotes/${emote}.png 1x, https://www.hearthcards.net/images/emotes/${emote}2.png 2x, https://www.hearthcards.net/images/emotes/${emote}3.png 4x`;
													return (
														<img
															key={emote}
															src={imgSrc}
															srcSet={imgSrcSet}
															alt={emote}
															className="emote-picker-img"
															onClick={() => handleEmoteClick(emote)}
														/>
													);
												})}
          </div>
        )}
      </div>
      <div className="d-flex justify-content-end align-items-center mt-2">
      {showStickyOption && (
          <div className="form-check me-2">
            <input
              type="checkbox"
              className="form-check-input"
              id="stickyComment"
              checked={isSticky}
              onChange={(e) => setIsSticky(e.target.checked)}
            />
            <label className="form-check-label" htmlFor="stickyComment">
              Pinned
            </label>
          </div>
        )}
      {hasDraft && (
        <Button 
          className="mx-1" 
          variant="outline-danger" 
          type="button" 
          onClick={clearDraft}
          disabled={!hasDraft}
        >
          Clear
        </Button>
      )}
        <Button 
          className="mx-1" 
          variant="primary" 
          type="button"
          onClick={() => setShowPicker(true)}
        >
          Attach {selectedCards.length > 0 ? `${selectedCards.length}` : ''}
        </Button>
        <Button 
          variant="primary" 
          type="submit"
          disabled={isSubmitting} // Disable while submitting
        >
          {isSubmitting ? 'Posting...' : isReply ? 'Reply' : 'Post'}
        </Button>
      </div>
      {showPicker && (
        <CardPicker
          type="attach"
          card={cardInfo.cardId2}
          onConfirm={handleCardSelection}
          onClose={() => setShowPicker(false)}
        />
      )}
    </form>
  );
};

export default CommentForm;